// ============================================
// ConverseTech Frontend - DET Phase Control Component
// ============================================
// UI component for controlling DET simulation phases

import { Clock, ChevronRight, CheckCircle } from 'lucide-react';
import type { DETPhaseConfig } from '@conversetech/shared';

interface DETPhaseControlProps {
  currentPhase: DETPhaseConfig | null;
  countdown: number | null;
  totalPhases: number;
  currentPhaseIndex: number;
  isOverTime: boolean;
  isWaitingForTask?: boolean;
  onNextPhase: () => void;
  disabled?: boolean;
}

export default function DETPhaseControl({
  currentPhase,
  countdown,
  totalPhases,
  currentPhaseIndex,
  isOverTime,
  isWaitingForTask = false,
  onNextPhase,
  disabled = false,
}: DETPhaseControlProps) {
  if (!currentPhase) return null;

  const totalTime = currentPhase.duration / 1000;
  const progress = countdown !== null && countdown > 0 ? countdown / totalTime : 0;
  const isLow = countdown !== null && countdown <= 10 && countdown > 5 && !isWaitingForTask;
  const isCritical = countdown !== null && countdown <= 5 && countdown > 0 && !isWaitingForTask;

  // Color classes based on timer state
  const timerColorClass = isOverTime
    ? 'text-red-500'
    : isCritical
    ? 'text-red-400'
    : isLow
    ? 'text-orange-400'
    : 'text-purple-100';

  const progressColorClass = isOverTime
    ? 'bg-red-500'
    : isCritical
    ? 'bg-red-500'
    : isLow
    ? 'bg-orange-500'
    : 'bg-purple-500';

  const bgColorClass = isOverTime
    ? 'bg-red-900/30 border-red-500'
    : 'bg-purple-900/50 border-purple-500';

  return (
    <div
      data-testid="det-phase-control"
      className={`${bgColorClass} border rounded-lg p-3 sm:p-4`}
    >
      {/* Phase Indicator */}
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div className="flex items-center gap-2">
          {/* Phase dots */}
          <div className="flex gap-1">
            {Array.from({ length: totalPhases }).map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx + 1 < currentPhaseIndex
                    ? 'bg-green-500'
                    : idx + 1 === currentPhaseIndex
                    ? 'bg-purple-400'
                    : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
          <span className="text-xs sm:text-sm text-purple-300">
            Phase {currentPhaseIndex}/{totalPhases}
          </span>
        </div>
        <span className="font-semibold text-sm sm:text-base text-purple-100">
          {currentPhase.label}
        </span>
      </div>

      {/* Timer Display */}
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
        <Clock className={`w-4 h-4 sm:w-5 sm:h-5 ${isWaitingForTask ? 'text-yellow-400 animate-pulse' : timerColorClass}`} />
        <span className={`text-xl sm:text-2xl font-mono font-bold ${isWaitingForTask ? 'text-yellow-400' : timerColorClass}`}>
          {isWaitingForTask ? (
            'Aguardando...'
          ) : isOverTime ? (
            <>Over Time! ({Math.abs(countdown ?? 0)}s)</>
          ) : (
            `${countdown ?? 0}s`
          )}
        </span>

        {/* Progress Bar */}
        <div className="flex-1 h-1.5 sm:h-2 bg-purple-950 rounded-full overflow-hidden">
          {isWaitingForTask ? (
            <div className="h-full w-full bg-yellow-500/50 animate-pulse" />
          ) : (
            <div
              className={`h-full transition-all duration-1000 ease-linear ${progressColorClass}`}
              style={{ width: `${Math.max(0, progress * 100)}%` }}
            />
          )}
        </div>
      </div>

      {/* Over Time Warning */}
      {isOverTime && (
        <div className="mb-2 sm:mb-3 p-2 bg-red-900/50 rounded text-xs sm:text-sm text-red-200">
          You're over time. Click "Next Phase" when ready to proceed.
        </div>
      )}

      {/* Next Phase Button */}
      <button
        onClick={onNextPhase}
        disabled={disabled || isWaitingForTask}
        data-testid="det-next-phase-button"
        className={`w-full py-2 sm:py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 touch-target ${
          isOverTime
            ? 'bg-red-600 hover:bg-red-700 active:bg-red-800'
            : 'bg-purple-600 hover:bg-purple-700 active:bg-purple-800'
        } disabled:bg-gray-600 disabled:cursor-not-allowed text-sm sm:text-base`}
      >
        {currentPhaseIndex < totalPhases ? (
          <>
            Next Phase
            <ChevronRight className="w-4 h-4" />
          </>
        ) : (
          <>
            Complete DET
            <CheckCircle className="w-4 h-4" />
          </>
        )}
      </button>
    </div>
  );
}
